/**
 * Database Abstraction Layer
 * Supports both Firebase (existing) and PostgreSQL API (new)
 * Routes PostgreSQL operations through backend API
 */

import { apiService } from './api.js';

// Firebase imports (existing)
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { db as firebaseDb } from '../firebase.js';

// Environment flag to switch between databases
const USE_POSTGRESQL = process.env.REACT_APP_USE_POSTGRESQL === 'true';

console.log(
  `🗄️ Database Mode: ${USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'}`
);

// Firebase operations (existing - READ-ONLY during migration)
const firebaseOperations = {
  async getUsers(search = '', limit = 10, offset = 0) {
    try {
      let usersQuery = collection(firebaseDb, 'users');
      usersQuery = query(usersQuery, orderBy('displayName'), limit(limit));

      if (search.trim() !== '') {
        usersQuery = query(
          usersQuery,
          where('displayName', '>', search),
          where('displayName', '<=', search + '\uf8ff'),
          limit(limit)
        );
      }

      const snapshot = await getDocs(usersQuery);
      const users = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { data: users, error: null };
    } catch (error) {
      console.error('Firebase getUsers error:', error);
      return { data: null, error: error.message };
    }
  },

  async getUserById(id) {
    try {
      const docSnap = await getDoc(doc(firebaseDb, 'users', id));
      if (docSnap.exists()) {
        return {
          data: { id: docSnap.id, ...docSnap.data() },
          error: null,
        };
      } else {
        return { data: null, error: 'User not found' };
      }
    } catch (error) {
      console.error('Firebase getUserById error:', error);
      return { data: null, error: error.message };
    }
  },

  async createUser(userData) {
    try {
      // SAFETY: Only allow creates during testing - comment out for read-only
      console.warn(
        '🛡️ SAFETY: User creation temporarily disabled during migration'
      );
      return { data: null, error: 'User creation disabled during migration' };
    } catch (error) {
      console.error('Firebase createUser error:', error);
      return { data: null, error: error.message };
    }
  },

  async updateUser(id, updates) {
    try {
      // SAFETY: Prevent updates during migration
      console.warn('🛡️ SAFETY: User updates disabled during migration');
      return { data: null, error: 'User updates disabled during migration' };
    } catch (error) {
      console.error('Firebase updateUser error:', error);
      return { data: null, error: error.message };
    }
  },

  async deleteUser(id) {
    try {
      // SAFETY: Prevent deletions during migration
      console.warn('🛡️ SAFETY: User deletion disabled during migration');
      return { data: null, error: 'User deletion disabled during migration' };
    } catch (error) {
      console.error('Firebase deleteUser error:', error);
      return { data: null, error: error.message };
    }
  },

  async getTransactions(userId) {
    try {
      const q = query(
        collection(firebaseDb, 'transactions'),
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      const transactions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { data: transactions, error: null };
    } catch (error) {
      console.error('Firebase getTransactions error:', error);
      return { data: null, error: error.message };
    }
  },

  async getDividends(year) {
    try {
      const q = query(
        collection(firebaseDb, 'dividends'),
        where('year', '==', year),
        orderBy('dividend_amount', 'desc')
      );
      const snapshot = await getDocs(q);
      const dividends = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return { data: dividends, error: null };
    } catch (error) {
      console.error('Firebase getDividends error:', error);
      return { data: null, error: error.message };
    }
  },
};

// Database abstraction layer
export const dbService = {
  // User operations
  async getUsers(search = '', limit = 10, offset = 0) {
    console.log(
      `📊 Getting users from ${USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'}`
    );

    if (USE_POSTGRESQL) {
      return await apiService.getUsers({
        search,
        limit,
        offset,
        orderBy: 'display_name',
        order: 'asc',
      });
    } else {
      return await firebaseOperations.getUsers(search, limit, offset);
    }
  },

  async getUserById(id) {
    console.log(
      `👤 Getting user ${id} from ${
        USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'
      }`
    );

    if (USE_POSTGRESQL) {
      return await apiService.getUserById(id);
    } else {
      return await firebaseOperations.getUserById(id);
    }
  },

  async createUser(userData) {
    console.log(
      `➕ Creating user in ${USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'}`
    );

    if (USE_POSTGRESQL) {
      return await apiService.createUser(userData);
    } else {
      return await firebaseOperations.createUser(userData);
    }
  },

  async updateUser(id, updates) {
    console.log(
      `✏️ Updating user ${id} in ${
        USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'
      }`
    );

    if (USE_POSTGRESQL) {
      return await apiService.updateUser(id, updates);
    } else {
      return await firebaseOperations.updateUser(id, updates);
    }
  },

  async deleteUser(id) {
    console.log(
      `🗑️ Deleting user ${id} from ${
        USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase'
      }`
    );

    if (USE_POSTGRESQL) {
      return await apiService.deleteUser(id);
    } else {
      return await firebaseOperations.deleteUser(id);
    }
  },

  async getTransactions(userId) {
    if (USE_POSTGRESQL) {
      return await apiService.getUserTransactions(userId);
    } else {
      return await firebaseOperations.getTransactions(userId);
    }
  },

  async getDividends(year) {
    if (USE_POSTGRESQL) {
      return await apiService.getDividends(year);
    } else {
      return await firebaseOperations.getDividends(year);
    }
  },

  async getSystemMetadata(key) {
    if (USE_POSTGRESQL) {
      return await apiService.getSystemMetadata(key);
    } else {
      // Firebase metadata fallback
      return { data: null, error: 'Metadata not implemented for Firebase' };
    }
  },

  async updateSystemMetadata(key, value, updatedBy) {
    if (USE_POSTGRESQL) {
      return await apiService.updateSystemMetadata(key, value, updatedBy);
    } else {
      // Firebase metadata fallback
      return {
        data: null,
        error: 'Metadata update not implemented for Firebase',
      };
    }
  },

  // Utility methods
  getCurrentDatabase() {
    return USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase';
  },

  isUsingPostgreSQL() {
    return USE_POSTGRESQL;
  },

  // Migration helper - compare data between databases
  async compareData(id) {
    console.log('🔍 Data comparison not available in API mode');
    return { error: 'Data comparison requires direct database access' };
  },
};

// Real-time subscriptions (Firebase specific for now)
export const subscriptions = {
  subscribeToUsers(callback) {
    if (USE_POSTGRESQL) {
      console.warn(
        '⚠️ Real-time subscriptions not yet implemented for PostgreSQL'
      );
      console.log('📊 Consider polling or implementing WebSocket solution');
      return () => {}; // Return empty unsubscribe function
    } else {
      const q = query(collection(firebaseDb, 'users'), orderBy('displayName'));
      return onSnapshot(q, (snapshot) => {
        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(users);
      });
    }
  },
};

// Migration status
export const migrationStatus = {
  database: USE_POSTGRESQL ? 'PostgreSQL API' : 'Firebase',
  isInMigration: process.env.REACT_APP_MIGRATION_MODE === 'true',
  safeMode: process.env.REACT_APP_SAFE_MODE === 'true',
};

export default dbService;
